import React, { useState, useEffect, useCallback } from 'react';
import { Gem, Send, Settings, Loader2, ExternalLink, Image } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/Skeleton';
import { TextArea } from '../../../components/TextArea';
import { Perola } from '../../../types/perolas';
import { enviarPerola, listarPerolas } from '../../../services/perolasService';
import { usePermissions, PERMISSOES } from '../../../hooks/usePermissions';
import { useToast } from '../../../hooks/use-toast';

function formatarData(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Tab: Enviar Pérola ────────────────────────────────────────────────────────

function TabEnviarPerola({ onSucesso }: { onSucesso: () => void }) {
  const [linkPrint, setLinkPrint] = useState('');
  const [descricao, setDescricao] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkPrint.trim() || !descricao.trim()) return;

    setIsSubmitting(true);
    try {
      await enviarPerola(linkPrint.trim(), descricao.trim());
      toast({ title: 'Pérola enviada!', description: 'Sua pérola foi registrada com sucesso.' });
      setLinkPrint('');
      setDescricao('');
      onSucesso();
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? 'Erro ao enviar pérola.';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Image className="w-5 h-5 text-yellow-500" />
          Registrar uma pérola
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Link do print <span className="text-destructive">*</span></label>
            <Input
              type="url"
              value={linkPrint}
              onChange={(e) => setLinkPrint(e.target.value)}
              placeholder="https://..."
              required
            />
            <p className="text-xs text-muted-foreground">
              Insira o link do print da pérola (Imgur, Discord, etc.)
            </p>
          </div>

          <TextArea
            title="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Descreva o momento que aconteceu..."
            rows={4}
            maxLength={1000}
          />
          <p className="text-xs text-muted-foreground text-right -mt-2">{descricao.length}/1000</p>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!linkPrint.trim() || !descricao.trim() || isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar pérola
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Tab: Gerenciar Pérolas ────────────────────────────────────────────────────

function TabGerenciarPerolas({ refresh }: { refresh: number }) {
  const [perolas, setPerolas] = useState<Perola[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const loadPerolas = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listarPerolas();
      setPerolas(data);
    } catch {
      toast({ title: 'Erro', description: 'Falha ao carregar pérolas.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadPerolas();
  }, [loadPerolas, refresh]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Pérolas registradas ({perolas.length})
        </h2>
        <Button variant="ghost" size="sm" onClick={loadPerolas}>
          Atualizar
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : perolas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Gem className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Nenhuma pérola registrada ainda.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {perolas.map((p) => (
            <Card key={p.id}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-sm font-medium">{p.remetente?.nick ?? '—'}</span>
                    {p.remetente?.patente && (
                      <span className="text-xs text-muted-foreground ml-1.5">
                        {p.remetente.patente.patente}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {formatarData(p.criado_em)}
                  </span>
                </div>

                <p className="text-sm">{p.descricao}</p>

                <a
                  href={p.link_print}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Ver print
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Página Principal ──────────────────────────────────────────────────────────

export function TiTiTiPerolas() {
  const [refreshGerenciar, setRefreshGerenciar] = useState(0);

  const { hasPermission } = usePermissions();

  const canEnviar = hasPermission(PERMISSOES.PEROLAS_ENVIAR);
  const canGerenciar = hasPermission(PERMISSOES.PEROLAS_GERENCIAR);

  const handleSucesso = () => {
    setRefreshGerenciar((prev) => prev + 1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-950 flex items-center justify-center">
          <Gem className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">TiTiTi Pérolas</h1>
          <p className="text-muted-foreground text-sm">
            Registre os melhores momentos e situações do dia a dia
          </p>
        </div>
      </div>

      <Tabs defaultValue={canEnviar ? 'enviar' : 'gerenciar'}>
        <TabsList>
          {canEnviar && (
            <TabsTrigger value="enviar" className="gap-2">
              <Send className="w-4 h-4" />
              Enviar pérola
            </TabsTrigger>
          )}
          {canGerenciar && (
            <TabsTrigger value="gerenciar" className="gap-2">
              <Settings className="w-4 h-4" />
              Gerenciar
            </TabsTrigger>
          )}
        </TabsList>

        {canEnviar && (
          <TabsContent value="enviar" className="mt-4">
            <TabEnviarPerola onSucesso={handleSucesso} />
          </TabsContent>
        )}

        {canGerenciar && (
          <TabsContent value="gerenciar" className="mt-4">
            <TabGerenciarPerolas refresh={refreshGerenciar} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
